import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import { toast } from 'react-toastify';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown time';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const RequestCommentsPanel = ({ requestId }) => {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');

  const { data, isLoading } = useQuery(
    ['request-comments', requestId],
    () => adminAPI.getRequestComments(requestId),
    {
      enabled: !!requestId,
      refetchOnWindowFocus: false
    }
  );

  const addCommentMutation = useMutation(
    (commentBody) => adminAPI.addRequestComment(requestId, { body: commentBody }),
    {
      onSuccess: () => {
        setBody('');
        queryClient.invalidateQueries(['request-comments', requestId]);
        queryClient.invalidateQueries(['activity-timeline', 'Request', requestId, 25]);
        toast.success('Comment added');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to add comment');
      }
    }
  );

  const comments = data?.comments || [];

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Internal Comments</h3>

        <div className="mb-6">
          <label htmlFor="request-comment-body" className="block text-sm font-medium text-gray-700 mb-2">
            Add Comment
          </label>
          <textarea
            id="request-comment-body"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add an internal comment for this request..."
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => addCommentMutation.mutate(body.trim())}
              disabled={addCommentMutation.isLoading || !body.trim()}
              className="inline-flex items-center rounded-md border border-transparent bg-brand-navy px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {addCommentMutation.isLoading ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-sm text-gray-500">Loading comments...</div>
        ) : comments.length === 0 ? (
          <div className="text-sm text-gray-500">No comments yet.</div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm font-medium text-gray-900">
                    {comment.User?.name || comment.User?.email || 'Unknown user'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatTimestamp(comment.created_at || comment.createdAt)}
                  </div>
                </div>
                <div className="mt-2 whitespace-pre-wrap text-sm text-gray-700">
                  {comment.body}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestCommentsPanel;
